import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  fetchHasAnyRegistration,
  fetchMyEventRegistration,
  fetchMyPastTeams,
  friendlyRegistrationError,
  type MyEventRegistration,
  type PastTeamSummary,
} from "@/api/registrations";
import type { UpcomingEvent } from "@/api/eventDiscovery";
import {
  fetchPersonProfileById,
  type PersonProfileFields,
} from "@/api/profile";
import { useToast } from "@/hooks/use-toast";
import { NotRegisteredView } from "./NotRegisteredView";
import { RegisteredView } from "./RegisteredView";
import { logger, serializeError } from "@/lib/logger";

interface Props {
  event: UpcomingEvent;
  personId: number;
  /** Called after any successful mutation so the parent list can refresh. */
  onChanged: () => void;
  /** Collapse the panel (e.g. after a cancellation). */
  onClose: () => void;
  /** Scroll the panel into view on mount. Off for always-on (solo) panels so
      the page doesn't jump to them on load. */
  autoScroll?: boolean;
}

/**
 * Inline registration panel rendered beneath an event card/row. Loads the
 * caller's registration state and swaps between the registered and
 * not-registered views. Mounts only while its event is the active one.
 */
export default function RegistrationPanel({
  event,
  personId,
  onChanged,
  onClose,
  autoScroll = true,
}: Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState<MyEventRegistration | null>(
    null,
  );
  const [pastTeams, setPastTeams] = useState<PastTeamSummary[]>([]);
  const [personProfile, setPersonProfile] =
    useState<PersonProfileFields | null>(null);
  // Gate the first-timer reassurance: only true when the person has zero
  // registrations anywhere. Defaults false so the line never flashes for
  // returning users before the check resolves.
  const [isNewcomer, setIsNewcomer] = useState(false);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Bring the freshly opened panel into view (helps the ?event_id= deep-link
  // and small screens). block:"nearest" keeps the movement minimal. Skipped for
  // always-on solo panels so the page doesn't auto-jump on load.
  useEffect(() => {
    if (!autoScroll) return;
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [autoScroll]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [reg, teams, hasAny, profile] = await Promise.all([
        fetchMyEventRegistration(event.event_id, personId),
        fetchMyPastTeams(personId),
        fetchHasAnyRegistration(personId),
        fetchPersonProfileById(personId),
      ]);
      setRegistration(reg);
      setPastTeams(teams);
      setIsNewcomer(!hasAny);
      setPersonProfile(profile);
    } catch (err) {
      logger.error("Failed to load registration", serializeError(err));
      toast({
        title: "Couldn't load registration details",
        description: "Please close and reopen.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [event.event_id, personId, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleAction = async (
    action: () => Promise<unknown>,
    successTitle: string,
    failureTitle: string,
    failureFallback: string,
  ): Promise<boolean> => {
    setBusy(true);
    try {
      await action();
      await reload();
      onChanged();
      toast({ title: successTitle });
      return true;
    } catch (err) {
      logger.error(failureTitle, serializeError(err));
      toast({
        title: failureTitle,
        description: friendlyRegistrationError(err, failureFallback),
        variant: "destructive",
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={panelRef}
      className="mt-3 animate-in fade-in slide-in-from-top-1 duration-200"
    >
      {loading ? (
        // Always-on (solo) panels skip the spinner so cards don't flash a
        // "Loading…" row on page load; the toggled team panel still shows it.
        autoScroll ? (
          <div className="flex items-center py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading…
          </div>
        ) : null
      ) : registration ? (
        <RegisteredView
          event={event}
          personId={personId}
          registration={registration}
          pastTeams={pastTeams}
          busy={busy}
          onAction={handleAction}
          onCancelled={() => {
            void reload();
            onChanged();
            onClose();
          }}
        />
      ) : (
        <NotRegisteredView
          event={event}
          pastTeams={pastTeams}
          isNewcomer={isNewcomer}
          personProfile={personProfile}
          onProfileSaved={reload}
          busy={busy}
          onAction={handleAction}
          onCancel={onClose}
        />
      )}
    </div>
  );
}
